import { ApiError, api, apiBaseUrl, authToken } from "./client";

/**
 * The PQX book flow, through the backend's `/api/worker/pqx` proxy.
 *
 * The engine (pqx_engine/) only extracts. A finished book is imported into
 * the question bank as `under_review` questions, reviewed in the normal
 * question editor, and published from the book page.
 */

export type BookState =
  | "queued"
  | "converting"
  | "extracting"
  | "checking_answers"
  | "done"
  | "failed"
  | "importing"
  | "imported"
  | "gate_failed"
  | "import_failed"
  /** Stopped by its uploader or an admin before it finished. */
  | "cancelled";

export type Band = "red" | "amber" | "green";

export interface Book {
  book_id: string;
  filename: string;
  state: BookState;
  subject: { id: string; name: string } | null;
  exam_type: string | null;
  reviewer: string;
  reviewer_name: string | null;
  note: string | null;
  progress: {
    pages_done: number;
    pages_total: number;
    percent: number;
    /** The pass after extraction (checking answers, rereading, ...); 0 outside one. */
    checks_done?: number;
    checks_total?: number;
    check_percent?: number;
    stage: string;
    detail: string;
  } | null;
  counts: {
    years: number;
    questions: number;
    answers: number;
    figures: number;
    with_topic: number;
  } | null;
  problems: string[];
  verdict: string | null;
  gates: string[];
  bands: Record<Band, number> | null;
  /** The questions are in and reviewable while the engine's checks still run. */
  early_review?: boolean;
  /**
   * The last time the book was sent back through the engine. `outcome` is set
   * once its result is merged into the bank. Null or absent: never rerun.
   */
  rerun?: {
    pages: string;
    by: string;
    at: string;
    count: number;
    outcome?: { updated: number; added: number; held: number; dropped: number } | null;
  } | null;
  imported: number;
  skipped: number;
  import_errors: string[];
  /** Place in line while `queued` (1 = next to start); null otherwise. */
  queue_position: number | null;
  /** A scanned book, or questions sent to the engine to check. Absent on older backends. */
  kind?: "book" | "verify";
  /** For a check: a file of new questions ("new"), or ones already in the bank ("fix"). */
  mode?: "new" | "fix" | null;
  created_at: string;
  updated_at: string;
}

/** One issue the engine raised on a question. */
export interface ReviewIssue {
  id: string;
  check: string;
  /** `stem`, `options.A`, `answer.key`, `figures`… */
  field: string | null;
  message: string;
  evidence: Record<string, unknown>;
  /**
   * The engine's proposed fix: the answer key (a letter), the options (a JSON
   * object), or the question text. Null when it has none to offer.
   */
  suggestion?: { field: "answer.key" | "options" | "stem"; value: string; by?: string } | null;
  /** A reviewer marked it as not a problem. */
  dismissed: boolean;
}

export interface BookQuestion {
  id: string;
  year: number;
  number: number;
  question_text: string;
  options: Record<string, string> | null;
  correct_answer: string | null;
  explanation: string | null;
  image_url: string | null;
  topic: string | null;
  subtopic: string | null;
  status: string;
  band: Band | null;
  checks: string[];
  issues: ReviewIssue[];
  open_issues: number;
  approved_by: string | null;
  approved_at: string | null;
  /** How many figures the engine cropped, and whether it saw one it could not. Null for old imports. */
  engine_figures: { cropped: number; uncropped: boolean } | null;
  /** The topic and subtopic the engine tagged it with, as it named them. Null for old imports. */
  engine_tags?: { topic: string | null; subtopic: string | null } | null;
  /** The printed question, cropped from its page, for a flagged question: to compare against its issues. */
  page_image_url?: string | null;
  /**
   * Imported while the engine's checks still run: no issues yet, and approving
   * opens when the checks finish. Absent on older backends.
   */
  checking?: boolean;
  /** The fields a reviewer changed while the checks ran; the engine's final values won't replace them. */
  edited?: string[];
  /** The engine dropped this question in its checks; kept because a reviewer had edited it. */
  dropped?: boolean;
  /**
   * The question was live when its book was rerun, so the rerun's new values
   * wait here as one-click suggestions rather than changing what students see.
   */
  rerun_live?: boolean;
  /**
   * A check of a bank question: what the engine would change. Only the
   * fields that differ are set. Null when it found nothing to change.
   */
  proposal?: Correction | null;
  /** What a reviewer did with the correction; null while undecided. */
  decision?: "accepted" | "kept" | null;
}

/** The engine's correction to a question already in the bank. */
export interface Correction {
  question_text?: string;
  options?: Record<string, string>;
  correct_answer?: string;
  topic?: string;
  subtopic?: string;
  /** What it changed, in its words. */
  changes: string[];
  /** Why it wants a person to look, when it couldn't fix it itself. */
  review_reason?: string;
}

/** A reviewer's own version of the correction, accepted instead of the engine's. */
export type CorrectionEdits = Partial<
  Pick<Correction, "question_text" | "options" | "correct_answer" | "topic" | "subtopic">
>;

/**
 * The engine's tags against the syllabus. `exists: false` is a name the bank
 * does not have yet, which the reviewer can add. `under` is the topic a
 * subtopic would be added beneath. Null when the engine kept no tags.
 */
export interface Tagging {
  topic: { name: string; exists: boolean } | null;
  subtopic: { name: string; exists: boolean; under: string | null } | null;
}

export interface ReviewItem {
  question: BookQuestion;
  tagging?: Tagging | null;
  /** 1-based place in the book, by year then number. */
  position: number;
  total: number;
  /** Questions still under review in the book. */
  remaining: number;
  previous_id: string | null;
  next_id: string | null;
  /** The next question still under review, wrapping round; null when all are done. */
  next_pending_id: string | null;
  /** "fix": bank questions the engine checked; each is decided by accepting or keeping its correction. */
  mode?: "new" | "fix" | null;
}

export type ReviewAction = "approve" | "return" | "dismiss" | "restore";

export interface CheckInfo {
  id: string;
  name: string;
  code: string;
  severity: string;
  field: string;
  message: string;
}

export interface EngineStatus {
  configured: boolean;
  mock: boolean;
  ready: boolean;
  accepts: string[];
  error?: string;
}

const base = "/api/worker/pqx";
const id = (value: string) => encodeURIComponent(value);

export const getStatus = () => api<EngineStatus>(`${base}/status`);
export const getChecks = () => api<{ checks: CheckInfo[] }>(`${base}/checks`);

export const listBooks = (mine = false) =>
  api<{ items: Book[] }>(`${base}/books${mine ? "?mine=1" : ""}`);
export const getBook = (bookId: string) => api<Book>(`${base}/books/${id(bookId)}`);
export const listBookQuestions = (bookId: string) =>
  api<{ items: BookQuestion[] }>(`${base}/books/${id(bookId)}/questions`);
export const retryImport = (bookId: string) =>
  api<Book>(`${base}/books/${id(bookId)}/import`, { method: "POST", body: {} });
export const publishQuestions = (bookId: string, questionIds?: string[]) =>
  api<{ published: number; skipped: { id: string; reason: string }[] }>(
    `${base}/books/${id(bookId)}/publish`,
    { method: "POST", body: questionIds ? { questionIds } : {} },
  );
export const firstToReview = (bookId: string) =>
  api<{ question_id: string | null }>(`${base}/books/${id(bookId)}/review/next`);
export const getReviewItem = (bookId: string, questionId: string) =>
  api<ReviewItem>(`${base}/books/${id(bookId)}/review/${id(questionId)}`);
/** Add the engine's topic, subtopic or both to the syllabus, and file the question under them. */
export const addReviewTags = (
  bookId: string,
  questionId: string,
  add: "topic" | "subtopic" | "both",
) =>
  api<ReviewItem>(`${base}/books/${id(bookId)}/review/${id(questionId)}/tags`, {
    method: "POST",
    body: { add },
  });

export const decideReview = (
  bookId: string,
  questionId: string,
  action: ReviewAction,
  issueId?: string,
) =>
  api<ReviewItem>(`${base}/books/${id(bookId)}/review/${id(questionId)}`, {
    method: "POST",
    body: issueId ? { action, issueId } : { action },
  });
/** Stop a queued or running book. It stays in the list as "Cancelled". */
export const cancelBook = (bookId: string) =>
  api<Book>(`${base}/books/${id(bookId)}/cancel`, { method: "POST", body: {} });

/**
 * Send a finished book back through the engine. It keeps every page and call
 * that worked and redoes only what failed; `pages` ("27" or "27-30") reads
 * those pages again from scratch. The book's questions are then updated in place.
 */
export const rerunBook = (bookId: string, pages?: string) =>
  api<Book>(`${base}/books/${id(bookId)}/rerun`, {
    method: "POST",
    body: pages ? { pages } : {},
  });

export const deleteBook = (bookId: string) =>
  api<void>(`${base}/books/${id(bookId)}`, { method: "DELETE" });

/** Most bank questions one check can take. */
export const VERIFY_MAX = 500;

/**
 * Send a JSON file of new questions (AI-generated, say) to the engine to check
 * and correct. The result imports as a book to review.
 */
export const verifyFile = (input: {
  subjectId: string;
  examType: "jamb" | "waec";
  filename: string;
  questions: unknown[];
}) => api<Book>(`${base}/verify/file`, { method: "POST", body: input });

/** Send questions already in the bank to be checked. Nothing changes until a reviewer accepts. */
export const verifyBank = (questionIds: string[]) =>
  api<Book>(`${base}/verify/bank`, { method: "POST", body: { questionIds } });

/**
 * Accept the engine's correction onto the bank question (or the reviewer's
 * edited version of it), or keep the question as it is. `force` accepts even
 * though the question changed since it was checked.
 */
export const decideCorrection = (
  bookId: string,
  questionId: string,
  action: "accept" | "keep",
  options: { edits?: CorrectionEdits; force?: boolean } = {},
) =>
  api<ReviewItem>(`${base}/books/${id(bookId)}/corrections/${id(questionId)}`, {
    method: "POST",
    body: { action, ...options },
  });

/** Accept every undecided correction the engine made on its own; held ones are left. */
export const acceptAllCorrections = (bookId: string) =>
  api<{ accepted: number; skipped: { id: string; reason: string }[] }>(
    `${base}/books/${id(bookId)}/corrections`,
    { method: "POST", body: {} },
  );

export interface UploadInput {
  file: File;
  subjectId: string;
  examType: "jamb" | "waec";
  /** "1-4" for a trial on the first four pages. */
  pages?: string;
  /** The file's SHA-256, so the backend can refuse a duplicate before reading it. */
  sha256?: string;
}

/** A Blob's bytes. `FileReader` rather than `blob.arrayBuffer()`, which older Safari lacks. */
function readBytes(file: Blob): Promise<ArrayBuffer> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as ArrayBuffer);
    reader.onerror = () => reject(reader.error ?? new Error("Couldn't read the file."));
    reader.readAsArrayBuffer(file);
  });
}

/** SHA-256 of a file, hex. Read in one go: books are at most 500 MB. */
export async function fileSha256(file: Blob): Promise<string> {
  const digest = await crypto.subtle.digest("SHA-256", new Uint8Array(await readBytes(file)));
  return [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, "0")).join("");
}

/** The live book with this content, if another upload already has it. */
export const findDuplicate = (sha256: string) =>
  api<{ duplicate: Book | null }>(`${base}/books/duplicate?sha256=${sha256}`);

/**
 * Upload a book, reporting progress (0–1).
 *
 * The file is the raw request body, not a form: the backend streams it on to
 * the engine without holding it, and XHR is what reports upload progress.
 */
export function uploadBook(input: UploadInput, onProgress?: (fraction: number) => void) {
  const query = new URLSearchParams({
    subjectId: input.subjectId,
    examType: input.examType,
    filename: input.file.name,
    ...(input.pages ? { pages: input.pages } : {}),
    ...(input.sha256 ? { sha256: input.sha256 } : {}),
  });
  return new Promise<Book>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("POST", `${apiBaseUrl}${base}/books?${query}`);
    const token = authToken.get();
    if (token) xhr.setRequestHeader("Authorization", `Bearer ${token}`);
    xhr.setRequestHeader("Content-Type", "application/octet-stream");
    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable) onProgress?.(event.loaded / event.total);
    };
    xhr.onload = () => {
      let body: unknown = null;
      try {
        body = JSON.parse(xhr.responseText);
      } catch {
        /* not JSON */
      }
      if (xhr.status >= 200 && xhr.status < 300) return resolve(body as Book);
      const error = (
        body as {
          error?: { code?: string; message?: string; details?: Record<string, unknown> };
        } | null
      )?.error;
      reject(
        new ApiError(
          xhr.status,
          error?.code ?? "UNKNOWN",
          error?.message ?? `The upload failed (${xhr.status}).`,
          error?.details ?? {},
        ),
      );
    };
    xhr.onerror = () => reject(new Error("The upload failed: the connection dropped."));
    xhr.send(input.file);
  });
}
